/**
 * The product picture in a centred window, the way buckmason.com shows it
 * (MM's reference, 23 Oct). It replaces the zoom inside the frame of
 * assets/mm-frame-zoom.js.
 *
 * From 750px wide, a click on a picture opens Horizon's own zoom window
 * (snippets/product-media-gallery-content.liquid, assets/zoom-dialog.js), but
 * styled by assets/mm-tokens.css, section 51: a box of 65% x 90% of the
 * screen, centred, with the page blurred behind; inside, only the picture
 * that was clicked, at the box's full width, explored by scrolling. Clicking
 * outside the box closes it; clicking the picture does not. Escape and the
 * cross close it as before.
 *
 * Below 750 there is no zoom at all, as on the reference: a tap on the
 * picture does nothing and the gallery only swipes.
 *
 * Every rule here works by holding back or letting through the native
 * `on:click` handlers, which assets/component.js serves from a capture
 * listener on `document`. This one is a capture listener on `window`, the
 * only node the event visits earlier.
 *
 * Loaded by blocks/_product-media-gallery.liquid when its "Picture in a
 * centred window" setting is on. If this file fails to load, the native
 * full-screen window opens as before.
 */

import { lockScroll, unlockScroll } from '@theme/utilities';

const WINDOW_CLASS = 'mm-zoom-window';
const CURRENT_CLASS = 'mm-zoom-window__current';
const SLIDE = 'media-gallery .product-media-container--zoomable:not(.dialog-zoomed-gallery *)';
const OPEN_INDEX = /\/open\/(\d+)/;

const wide = window.matchMedia('(min-width: 750px)');

// The native window opens after a view transition; if it has not opened by
// then, the lock taken for it is let go.
const OPEN_TIMEOUT = 1500;

// Below 750 the picture opens nothing, so section 51 takes the magnifier
// cursor off it there; this says the module is running.
document.documentElement.setAttribute('data-mm-zoom-window', '');

/**
 * Marks the window and the one picture it should show, before the native
 * handler opens it. Done on every click and not once on load: changing the
 * variant re-renders the gallery, window included.
 *
 * The window opens with the focus on its cross, and Chrome rings it even
 * after a mouse click. `data-mm-pointer` lets section 51 drop the ring then;
 * opened from the keyboard, it stays.
 *
 * And it locks the page behind, which Horizon's window never needed: it
 * covered the whole screen. Its `scroll-lock` attribute only works on a
 * <details> (assets/dialog.js:175-190).
 * @param {HTMLElement} slide
 * @param {boolean} byPointer
 */
function prepare(slide, byPointer) {
  const index = Number(slide.getAttribute('on:click')?.match(OPEN_INDEX)?.[1]);
  const dialog = slide.closest('media-gallery')?.querySelector('zoom-dialog dialog');
  if (!dialog || Number.isNaN(index)) return;

  dialog.classList.add(WINDOW_CLASS);
  dialog.toggleAttribute('data-mm-pointer', byPointer);
  dialog.querySelectorAll('.dialog-zoomed-gallery > li').forEach((item, i) => {
    item.classList.toggle(CURRENT_CLASS, i === index);
  });

  lockScroll(dialog);
  dialog.addEventListener('close', () => unlockScroll(dialog), { once: true });
  setTimeout(() => {
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) unlockScroll(dialog);
  }, OPEN_TIMEOUT);
}

/**
 * @param {MouseEvent} event
 * @param {HTMLDialogElement} dialog
 */
function isOutside(event, dialog) {
  const rect = dialog.getBoundingClientRect();
  return (
    event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom
  );
}

window.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const dialog = target.closest(`dialog.${WINDOW_CLASS}[open]`);
    if (dialog instanceof HTMLDialogElement) {
      if (target.closest('.dialog-zoomed-gallery__close-button')) return;

      // The backdrop belongs to the dialog itself: a click there lands on it.
      if (target === dialog && event.detail !== 0 && isOutside(event, dialog)) {
        event.stopPropagation();
        dialog.querySelector('.dialog-zoomed-gallery__close-button')?.click();
        return;
      }

      // Every picture in the window carries `on:click="/close"`.
      if (target.closest('.dialog-zoomed-gallery > li')) event.stopPropagation();
      return;
    }

    const slide = target.closest(SLIDE);
    if (!(slide instanceof HTMLElement)) return;

    if (!wide.matches) {
      event.stopPropagation();
      return;
    }

    prepare(slide, event.detail !== 0);
  },
  true
);
